import React, { useMemo, useRef, useState, useEffect } from "react";
import { COUNTRIES } from "./ReservaShared";

/*
 * Reusable country-code + phone number input, extracted from the buyer
 * checkout form pattern (e.g. ClubDetailDesktop.jsx). Controlled component:
 * the parent owns `countryCode` and `number` state and receives updates via
 * `onChange({ countryCode, number, valid })`. Validation delegates to each
 * COUNTRIES entry's own `validate(val)`.
 */
export default function PhoneCountryInput({
  countryCode,
  number,
  onChange,
  error,
  placeholder,
  inputProps = {},
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const countryRef = useRef(null);

  const selectedCountry = useMemo(
    () => COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0],
    [countryCode]
  );

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const handleClickOutside = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const emit = (nextCountryCode, nextNumber) => {
    const country = COUNTRIES.find((c) => c.code === nextCountryCode) || COUNTRIES[0];
    const valid = Boolean(nextNumber) && country.validate(nextNumber);
    onChange?.({ countryCode: nextCountryCode, number: nextNumber, valid });
  };

  return (
    <div>
      <div
        className={`inp phone-country-input${error ? " is-invalid" : ""}`}
        style={{ paddingLeft: 0, position: "relative", display: "flex", alignItems: "center", height: 44, border: "1.5px solid #e5e7eb", borderRadius: 12, background: "#ffffff" }}
      >
        <div
          ref={countryRef}
          style={{
            display: "flex", alignItems: "center", gap: "8px", paddingLeft: "14px",
            paddingRight: "12px", height: "100%", background: "#f9fafb",
            borderTopLeftRadius: "10.5px", borderBottomLeftRadius: "10.5px", userSelect: "none",
            borderRight: "1px solid var(--zoco-border, #E5E7EB)", flexShrink: 0, cursor: "pointer", position: "relative",
          }}
          onClick={() => setDropdownOpen((o) => !o)}
        >
          {selectedCountry.flag}
          <span style={{ fontSize: "13px", fontWeight: 700, color: "#2d3035", whiteSpace: "nowrap" }}>{selectedCountry.label}</span>
          <svg width="8" height="5" viewBox="0 0 8 5" fill="none" style={{ marginLeft: "2px", transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
            <path d="M1 1L4 4L7 1" stroke="#4b5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {dropdownOpen && (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, background: "#ffffff", border: "1px solid #E5E7EB", borderRadius: "10px", boxShadow: "0 10px 25px rgba(0,0,0,0.12)", zIndex: 1000, minWidth: "160px", padding: "6px 0", maxHeight: "200px", overflowY: "auto" }}>
              {COUNTRIES.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(false);
                    emit(c.code, number);
                  }}
                  style={{ display: "flex", alignItems: "center", width: "100%", padding: "8px 14px", border: "none", background: selectedCountry.code === c.code ? "#f3f4f6" : "#ffffff", cursor: "pointer", gap: "10px", textAlign: "left" }}
                >
                  {c.flag}
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#2d3035", flex: 1 }}>{c.label}</span>
                  {selectedCountry.code === c.code && <span style={{ color: "#00b159", fontWeight: "bold", fontSize: "14px" }}>✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          type="tel"
          placeholder={placeholder || selectedCountry.placeholder}
          value={number}
          onChange={(e) => emit(selectedCountry.code, e.target.value)}
          style={{ border: "none", outline: "none", height: "100%", width: "100%", paddingLeft: "12px", fontSize: "14.5px", fontWeight: 600, color: "var(--zoco-ink, #2d3035)", background: "transparent" }}
          {...inputProps}
        />
      </div>
    </div>
  );
}
